import { useEffect, useMemo, useState } from "react";
import { Loader2, Plus, ShoppingBag, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, API_BASE } from "../../lib/api";

const darkInput = "w-full rounded-lg border border-adminborder bg-adminbg px-3 py-2 text-sm text-slate-200 outline-none focus:border-saffron";
const STATUSES = ["placed", "confirmed", "packed", "shipped", "delivered", "cancelled"];
const ORDER_BADGE = { placed: "bg-sky-500/15 text-sky-400", confirmed: "bg-indigo-500/15 text-indigo-400", packed: "bg-amber-500/15 text-amber-400", shipped: "bg-violet-500/15 text-violet-400", delivered: "bg-emerald-500/15 text-emerald-400", cancelled: "bg-red-500/15 text-red-400" };

export default function AdminShop() {
  const [tab, setTab] = useState("products");
  const [cats, setCats] = useState([]);
  const [prods, setProds] = useState([]);
  const [orders, setOrders] = useState([]);
  const [catForm, setCatForm] = useState({ name: "", subs: "" });
  const [editCat, setEditCat] = useState(null);
  const [prodForm, setProdForm] = useState({ name: "", price: "", category_id: "", subcategory: "", description: "", stock: "", image_path: "" });
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [adForm, setAdForm] = useState({ image_path: "", heading: "", text: "", active: false });
  const [adUploading, setAdUploading] = useState(false);

  const load = () => {
    api.get("/shop/admin/categories").then((r) => setCats(r.data)).catch(() => {});
    api.get("/shop/admin/products").then((r) => setProds(r.data)).catch(() => {});
    api.get("/shop/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/shop/admin/bazaar-ad").then((r) => setAdForm(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const selectedCat = useMemo(() => cats.find((c) => c.id === prodForm.category_id), [cats, prodForm.category_id]);

  const saveCategory = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { name: catForm.name, subs: catForm.subs.split(",").map((s) => s.trim()).filter(Boolean) };
    try {
      if (editCat) await api.put(`/shop/admin/categories/${editCat}`, payload);
      else await api.post("/shop/admin/categories", payload);
      toast.success(editCat ? "Category updated" : "Category added");
      setCatForm({ name: "", subs: "" }); setEditCat(null); load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const deleteCategory = async (c) => {
    if (!window.confirm(`Delete category "${c.name}"?`)) return;
    try { await api.delete(`/shop/admin/categories/${c.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/uploads", fd);
      setProdForm((f) => ({ ...f, image_path: res.data.path }));
      toast.success("Image uploaded");
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploading(false); }
  };

  const saveProduct = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/shop/admin/products", { ...prodForm, price: parseFloat(prodForm.price) || 0, stock: parseInt(prodForm.stock) || 0 });
      toast.success("Product added");
      setProdForm({ name: "", price: "", category_id: "", subcategory: "", description: "", stock: "", image_path: "" });
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const deleteProduct = async (p) => {
    if (!window.confirm(`Delete product "${p.name}"?`)) return;
    try { await api.delete(`/shop/admin/products/${p.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const setOrderStatus = async (o, status) => {
    try { await api.put(`/shop/admin/orders/${o.id}`, { status }); toast.success(`Order → ${status}`); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const TabBtn = ({ k, label, count }) => (
    <button onClick={() => setTab(k)} data-testid={`shop-tab-${k}`}
            className={`rounded-full px-4 py-2 text-sm font-bold ${tab === k ? "bg-saffron text-white" : "bg-admincard text-slate-400 hover:text-white"}`}>
      {label}{count !== undefined ? ` (${count})` : ""}
    </button>
  );

  return (
    <div data-testid="admin-shop">
      <h1 className="font-heading text-2xl font-bold text-white">Shop Manager</h1>
      <p className="mt-1 text-sm text-slate-500">Categories, subcategories, products and customer orders for the homepage shop.</p>
      <div className="mt-5 flex gap-2">
        <TabBtn k="products" label="Products" count={prods.length} />
        <TabBtn k="categories" label="Categories" count={cats.length} />
        <TabBtn k="orders" label="Orders" count={orders.length} />
        <TabBtn k="bazaar" label="Bazaar Ad" />
      </div>

      {tab === "categories" && (
        <div className="mt-6 grid gap-6 lg:grid-cols-3">
          <form onSubmit={saveCategory} className="rounded-xl border border-adminborder bg-admincard p-5">
            <p className="text-sm font-bold text-white">{editCat ? "Edit Category" : "Add Category"}</p>
            <input required className={`${darkInput} mt-3`} data-testid="cat-name" placeholder="Category name (e.g. Packing Boxes)" value={catForm.name} onChange={(e) => setCatForm({ ...catForm, name: e.target.value })} />
            <input className={`${darkInput} mt-2`} data-testid="cat-subs" placeholder="Subcategories, comma separated" value={catForm.subs} onChange={(e) => setCatForm({ ...catForm, subs: e.target.value })} />
            <div className="mt-3 flex gap-2">
              <button disabled={busy} data-testid="cat-save-btn" className="flex items-center gap-1.5 rounded-lg bg-saffron px-4 py-2 text-xs font-bold text-white hover:bg-saffron-hover disabled:opacity-60">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} {editCat ? "Update" : "Add"}
              </button>
              {editCat && <button type="button" onClick={() => { setEditCat(null); setCatForm({ name: "", subs: "" }); }} className="rounded-lg border border-adminborder px-3 py-2 text-xs text-slate-400">Cancel</button>}
            </div>
          </form>
          <div className="rounded-xl border border-adminborder bg-admincard lg:col-span-2">
            <p className="border-b border-adminborder px-5 py-3.5 text-sm font-bold text-white">All Categories</p>
            <div className="divide-y divide-adminborder/60" data-testid="admin-cat-list">
              {cats.map((c) => (
                <div key={c.id} className="flex items-center justify-between gap-3 px-5 py-3" data-testid={`admin-cat-${c.slug}`}>
                  <div>
                    <p className="text-sm font-bold text-white">{c.name}</p>
                    <p className="text-xs text-slate-500">{c.subs?.length ? c.subs.join(" · ") : "No subcategories"}</p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => { setEditCat(c.id); setCatForm({ name: c.name, subs: (c.subs || []).join(", ") }); }} data-testid={`cat-edit-${c.slug}`} className="rounded-lg bg-white/5 px-3 py-1.5 text-xs font-bold text-slate-300 hover:bg-white/10">Edit</button>
                    <button onClick={() => deleteCategory(c)} data-testid={`cat-delete-${c.slug}`} className="rounded-lg bg-red-500/10 px-3 py-1.5 text-xs font-bold text-red-400 hover:bg-red-500/20"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
              ))}
              {!cats.length && <p className="px-5 py-8 text-center text-sm text-slate-500">No categories yet — add your first one.</p>}
            </div>
          </div>
        </div>
      )}

      {tab === "products" && (
        <div className="mt-6 grid gap-6 lg:grid-cols-3">
          <form onSubmit={saveProduct} className="rounded-xl border border-adminborder bg-admincard p-5">
            <p className="text-sm font-bold text-white">Add Product</p>
            <div className="mt-3 space-y-2">
              <input required className={darkInput} data-testid="prod-name" placeholder="Product name" value={prodForm.name} onChange={(e) => setProdForm({ ...prodForm, name: e.target.value })} />
              <div className="grid grid-cols-2 gap-2">
                <input required type="number" step="0.01" min="0" className={darkInput} data-testid="prod-price" placeholder="Price ₹" value={prodForm.price} onChange={(e) => setProdForm({ ...prodForm, price: e.target.value })} />
                <input required type="number" min="0" className={darkInput} data-testid="prod-stock" placeholder="Stock" value={prodForm.stock} onChange={(e) => setProdForm({ ...prodForm, stock: e.target.value })} />
              </div>
              <select required className={darkInput} data-testid="prod-category" value={prodForm.category_id} onChange={(e) => setProdForm({ ...prodForm, category_id: e.target.value, subcategory: "" })}>
                <option value="">Select category…</option>
                {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              {selectedCat?.subs?.length > 0 && (
                <select className={darkInput} data-testid="prod-subcategory" value={prodForm.subcategory} onChange={(e) => setProdForm({ ...prodForm, subcategory: e.target.value })}>
                  <option value="">Subcategory (optional)…</option>
                  {selectedCat.subs.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              )}
              <textarea rows={2} className={darkInput} data-testid="prod-description" placeholder="Description (optional)" value={prodForm.description} onChange={(e) => setProdForm({ ...prodForm, description: e.target.value })} />
              <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-adminborder px-3 py-2.5 text-xs font-semibold text-slate-400 hover:border-saffron hover:text-saffron" data-testid="prod-image-upload">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {prodForm.image_path ? "Image uploaded ✓ (click to replace)" : "Upload product image"}
                <input type="file" accept="image/*" className="hidden" onChange={uploadImage} />
              </label>
            </div>
            <button disabled={busy || uploading} data-testid="prod-save-btn" className="mt-3 flex items-center gap-1.5 rounded-lg bg-saffron px-4 py-2 text-xs font-bold text-white hover:bg-saffron-hover disabled:opacity-60">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Add Product
            </button>
          </form>
          <div className="grid content-start gap-3 lg:col-span-2 sm:grid-cols-2" data-testid="admin-product-list">
            {prods.map((p) => (
              <div key={p.id} className="flex gap-3 rounded-xl border border-adminborder bg-admincard p-3" data-testid={`admin-prod-${p.id}`}>
                <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-adminbg">
                  {p.image_path ? <img src={`${API_BASE}/api/shop/public/image/${p.id}`} alt={p.name} className="h-full w-full object-cover" /> : <ShoppingBag className="m-4 h-8 w-8 text-slate-600" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-bold text-white">{p.name}</p>
                  <p className="text-[11px] text-slate-500">{p.category_name}{p.subcategory ? ` · ${p.subcategory}` : ""} · stock {p.stock}</p>
                  <p className="text-sm font-extrabold text-saffron">₹{p.price}</p>
                </div>
                <button onClick={() => deleteProduct(p)} data-testid={`prod-delete-${p.id}`} className="self-start rounded-lg bg-red-500/10 p-2 text-red-400 hover:bg-red-500/20"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            {!prods.length && <p className="rounded-xl border border-adminborder bg-admincard px-5 py-8 text-center text-sm text-slate-500 sm:col-span-2">No products yet.</p>}
          </div>
        </div>
      )}

      {tab === "bazaar" && (
        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          <div className="rounded-xl border border-adminborder bg-admincard p-5">
            <p className="text-sm font-bold text-white">Bazaar Advertisement on Invoices</p>
            <p className="mt-1 text-xs text-slate-500">This strip appears on every customer invoice & receipt PDF.</p>
            <div className="mt-4 space-y-3">
              <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-adminborder px-3 py-2.5 text-xs font-semibold text-slate-400 hover:border-saffron hover:text-saffron" data-testid="bazaar-ad-upload">
                {adUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {adForm.image_path ? "Banner uploaded ✓ (click to replace)" : "Upload wide banner image"}
                <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  setAdUploading(true);
                  try {
                    const fd = new FormData(); fd.append("file", file);
                    const res = await api.post("/uploads", fd);
                    setAdForm((f) => ({ ...f, image_path: res.data.path }));
                    toast.success("Banner uploaded");
                  } catch (err) { toast.error(apiError(err)); }
                  finally { setAdUploading(false); }
                }} />
              </label>
              <p className="rounded-lg bg-saffron/10 px-3 py-2 text-[11px] font-semibold text-saffron" data-testid="bazaar-ad-size-hint">
                Recommended size: 1600 × 400 px (wide 4:1 banner) · PNG/JPG · max 5 MB
              </p>
              <input className={darkInput} data-testid="bazaar-ad-heading" placeholder="Heading (e.g. India's Bazaar, IndiaFast Delivery)" value={adForm.heading} onChange={(e) => setAdForm({ ...adForm, heading: e.target.value })} />
              <textarea rows={3} className={darkInput} data-testid="bazaar-ad-text" placeholder="Ad details (e.g. Order Indian food & gifts at www.indiafast.in/shop — we deliver worldwide)" value={adForm.text} onChange={(e) => setAdForm({ ...adForm, text: e.target.value })} />
              <label className="flex items-center gap-2 text-sm font-semibold text-slate-300">
                <input type="checkbox" checked={adForm.active} data-testid="bazaar-ad-active"
                       onChange={(e) => setAdForm({ ...adForm, active: e.target.checked })}
                       className="h-4 w-4 accent-[#FF5A1F]" />
                Show ad strip on all invoices & receipts
              </label>
              <button disabled={busy || adUploading} data-testid="bazaar-ad-save"
                      onClick={async () => {
                        setBusy(true);
                        try { await api.put("/shop/admin/bazaar-ad", adForm); toast.success("Bazaar ad saved"); }
                        catch (err) { toast.error(apiError(err)); }
                        finally { setBusy(false); }
                      }}
                      className="flex items-center gap-1.5 rounded-lg bg-saffron px-4 py-2 text-xs font-bold text-white hover:bg-saffron-hover disabled:opacity-60">
                {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Save Advertisement
              </button>
            </div>
          </div>
          <div className="rounded-xl border border-adminborder bg-admincard p-5">
            <p className="text-sm font-bold text-white">Preview</p>
            <div className="mt-4 overflow-hidden rounded-xl bg-[#0A1128] p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-[8px] font-bold uppercase tracking-wider text-saffron">INDIAFAST BAZAAR</p>
                  <p className="mt-1 font-heading text-sm font-bold text-white" data-testid="bazaar-preview-heading">{adForm.heading || "India's Bazaar, IndiaFast Delivery"}</p>
                  <p className="mt-1 text-[10px] text-slate-400" data-testid="bazaar-preview-text">{adForm.text || "Your ad details will appear here"}</p>
                </div>
                {adForm.image_path && (
                  <img src={`${API_BASE}/api/files/${adForm.image_path}?auth=${localStorage.getItem("if_token")}`} alt="Bazaar banner"
                       className="h-14 w-28 rounded-lg object-cover" data-testid="bazaar-preview-img" />
                )}
              </div>
            </div>
            <p className={`mt-3 text-xs font-bold ${adForm.active ? "text-emerald-400" : "text-slate-500"}`} data-testid="bazaar-preview-status">
              {adForm.active ? "● LIVE on all invoices & receipts" : "○ Inactive — not shown on documents"}
            </p>
          </div>
        </div>
      )}

      {tab === "orders" && (
        <div className="mt-6 overflow-x-auto rounded-xl border border-adminborder bg-admincard">
          <table className="w-full min-w-[820px] text-sm" data-testid="admin-orders-table">
            <thead>
              <tr className="border-b border-adminborder text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-5 py-3">Order</th><th className="px-5 py-3">Customer</th><th className="px-5 py-3">Items</th>
                <th className="px-5 py-3">Total</th><th className="px-5 py-3">Address</th><th className="px-5 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-b border-adminborder/60 last:border-0" data-testid={`admin-order-${o.order_id}`}>
                  <td className="px-5 py-3 font-mono text-xs font-bold text-saffron">{o.order_id}
                    <p className="text-[10px] font-normal text-slate-500">{new Date(o.created_at).toLocaleDateString("en-IN")}</p></td>
                  <td className="px-5 py-3 text-xs text-slate-300">{o.customer_name}<p className="text-slate-500">{o.phone}</p></td>
                  <td className="max-w-[200px] px-5 py-3 text-xs text-slate-300">{o.items.map((i) => `${i.name} ×${i.qty}`).join(", ")}</td>
                  <td className="px-5 py-3 text-sm font-bold text-white">₹{o.total}
                    <p className={`text-[10px] font-bold ${o.payment_status === "paid" ? "text-emerald-400" : "text-amber-400"}`} data-testid={`admin-order-pay-${o.order_id}`}>{o.payment_status === "paid" ? "PAID ONLINE" : "UNPAID / COD"}</p></td>
                  <td className="max-w-[180px] px-5 py-3 text-xs text-slate-400">{o.address}, {o.city} {o.pincode}</td>
                  <td className="px-5 py-3">
                    <span className={`mb-1 inline-block rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${ORDER_BADGE[o.status]}`} data-testid={`admin-order-status-${o.order_id}`}>{o.status}</span>
                    <select className={`${darkInput} block py-1 text-xs`} data-testid={`admin-order-status-sel-${o.order_id}`} value={o.status} onChange={(e) => setOrderStatus(o, e.target.value)}>
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <button onClick={async () => { if (window.confirm(`Delete order ${o.order_id}?`)) { try { await api.delete(`/shop/admin/orders/${o.id}`); toast.success("Order deleted"); load(); } catch (err) { toast.error(apiError(err)); } } }}
                            data-testid={`admin-order-delete-${o.order_id}`}
                            className="mt-1.5 flex items-center gap-1 rounded-lg bg-red-500/10 px-2.5 py-1 text-[10px] font-bold text-red-400 hover:bg-red-500/20">
                      <Trash2 className="h-3 w-3" /> Delete
                    </button>
                  </td>
                </tr>
              ))}
              {!orders.length && <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-500" data-testid="admin-orders-empty">No orders yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
